package com.qrkontaq.data.repository

import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.data.dto.PlatformOverviewDto
import com.qrkontaq.data.dto.PlatformPaymentDto
import com.qrkontaq.data.dto.PlatformRestaurantDto
import com.qrkontaq.data.dto.WebAnalyticsDto
import com.qrkontaq.data.dto.WebSessionDto
import com.qrkontaq.data.http.ApiResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.http.isSuccess

/** Outcome of a platform-dashboard call; Denied = signed in but not the platform owner. */
sealed interface PlatformResult<out T> {
    data class Ok<T>(val value: T) : PlatformResult<T>

    data object Unauthorized : PlatformResult<Nothing>

    data object Denied : PlatformResult<Nothing>

    data object Error : PlatformResult<Nothing>
}

/** Cross-venue business stats for the platform owner (adminkontaq subdomain). */
class PlatformRepository(
    private val client: HttpClient,
) {
    suspend fun overview(): PlatformResult<PlatformOverviewDto> = fetch("v1/platform/overview")

    suspend fun restaurants(): PlatformResult<List<PlatformRestaurantDto>> = fetch("v1/platform/restaurants")

    suspend fun payments(limit: Int = 100): PlatformResult<List<PlatformPaymentDto>> =
        fetch("v1/platform/payments?limit=$limit")

    suspend fun webAnalytics(
        days: Int = 30,
        site: String? = null,
    ): PlatformResult<WebAnalyticsDto> =
        fetch("v1/platform/analytics?days=$days" + (site?.let { "&site=$it" } ?: ""))

    suspend fun webSessions(
        days: Int = 30,
        site: String? = null,
        limit: Int = 50,
    ): PlatformResult<List<WebSessionDto>> =
        fetch("v1/platform/analytics/sessions?days=$days&limit=$limit" + (site?.let { "&site=$it" } ?: ""))

    /** Hard-deletes a venue with its full cascade (menu, tables, orders, staff, media). */
    suspend fun deleteRestaurant(id: String): PlatformResult<Unit> =
        deleteCall("v1/platform/restaurants/$id")

    /** Hard-deletes a user account together with every venue they own. */
    suspend fun deleteUser(id: String): PlatformResult<Unit> =
        deleteCall("v1/platform/users/$id")

    private suspend fun deleteCall(path: String): PlatformResult<Unit> =
        try {
            val response = client.delete(AppConfig.getApiUrl(path))
            when {
                response.status.value == 401 -> PlatformResult.Unauthorized
                response.status.value == 403 -> PlatformResult.Denied
                !response.status.isSuccess() -> PlatformResult.Error
                else -> PlatformResult.Ok(Unit)
            }
        } catch (_: Throwable) {
            PlatformResult.Error
        }

    private suspend inline fun <reified T> fetch(path: String): PlatformResult<T> =
        try {
            val response = client.get(AppConfig.getApiUrl(path))
            when {
                response.status.value == 401 -> PlatformResult.Unauthorized
                response.status.value == 403 -> PlatformResult.Denied
                !response.status.isSuccess() -> PlatformResult.Error
                else ->
                    response.body<ApiResponse<T>>().data
                        ?.let { PlatformResult.Ok(it) }
                        ?: PlatformResult.Error
            }
        } catch (_: Throwable) {
            PlatformResult.Error
        }
}
